import { formatValue } from '@clockwise/report-kit';
import './dashboard-theme.css';

/**
 * Named series colours. Charts reference `SERIES.retainer`, never a raw
 * variable or hex, so one engagement type reads the same in every tile and
 * the palette can be re-themed in one file.
 */
export const SERIES = {
  retainer: 'var(--dash-retainer)',
  hourly: 'var(--dash-hourly)',
  free: 'var(--dash-free)',
  internal: 'var(--dash-internal)',
  leave: 'var(--dash-leave)',
  idle: 'var(--dash-idle)',
  /** Billability split, used inside a single engagement type. */
  billable: 'var(--dash-retainer)',
  nonBillable: 'var(--dash-hourly)',
  /** Totals and axis-adjacent marks. */
  neutral: 'var(--dash-neutral)',
  positive: 'var(--dash-positive)',
  negative: 'var(--dash-negative)',
} as const;

/** Chrome shared by every chart, so axes and grids match across tiles. */
export const AXIS = {
  tick: { fontSize: 11, fill: 'var(--muted-foreground)' },
  tickSmall: { fontSize: 9.5, fill: 'var(--muted-foreground)' },
  line: 'var(--border)',
  label: {
    fontSize: 10.5,
    fontWeight: 700,
    fill: 'var(--foreground)',
    // Printed values sit above/beside bars in a column; proportional digits
    // make them ragged.
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em',
  },
} as const;

/** Inline style for Recharts' tooltip, which can't be reached with a class. */
export const TOOLTIP_STYLE = {
  background: 'var(--popover)',
  color: 'var(--popover-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontSize: 12,
} as const;

/** Cursor highlight behind the hovered bar. */
export const TOOLTIP_CURSOR = { fill: 'var(--muted)', opacity: 0.5 } as const;

/**
 * Recharts hands tooltip formatters a loosely-typed value (it can't know a
 * series is numeric). These narrow it once, so call sites read as plain
 * number/string code instead of repeating the coercion.
 */
export function toNumber(value: unknown): number {
  return typeof value === 'number' ? value : Number(value ?? 0);
}

export function toKey(name: unknown): string {
  return String(name ?? '');
}

/**
 * Hours, at the one precision the product reads them.
 *
 * It used to round to whole hours on the claim that "hour counts are never
 * fractional here" — true of the fixtures it is fed today, and false of
 * `timesheets.hours`, which is `numeric(6, 2)`. The moment these tiles read
 * live data the same measure would have read 950 here and 950.00 in the report
 * builder beside it, so this defers to the shared rule rather than being a
 * third answer.
 */
export function formatHours(value: number): string {
  return formatValue(value, 'hours');
}

/** `part` as a whole-number percentage of `whole`; 0 when `whole` is 0. */
export function percentOf(part: number, whole: number): number {
  return whole ? Math.round((part / whole) * 100) : 0;
}

/**
 * `part` as a percentage of `whole` to 2dp, for plotting rather than display.
 *
 * Guards the zero denominator that {@link percentOf} guards: a group with no
 * available hours (everyone on leave, a location opened mid-month) would
 * otherwise yield NaN or Infinity, which Recharts plots as a silently missing
 * bar rather than an error.
 */
export function sharePercent(part: number, whole: number): number {
  return whole ? Number(((part / whole) * 100).toFixed(2)) : 0;
}
